// src/CustomEdge.js
/**
 * 自定义连线组件
 * 
 * 核心功能：
 * 1. 🎯 抗缩放（Anti-Zoom）：连线粗细在所有缩放级别下保持视觉一致
 *    - strokeWidth / zoom：放大时连线不变粗，缩小时不变细
 *    - 触控区域 / zoom：确保任何缩放下鼠标都能准确悬停
 *    - 动画圆点 / zoom：传输动画球的大小也保持恒定
 * 
 * 2. ✂️ 悬停删除：鼠标悬停显示剪刀图标，点击删除连线
 * 3. 🔒 锁定保护：locked 状态的连线不可删除
 * 4. ✨ 传输动画：isTransmitting 时显示移动的白色光点
 * 5. 🧹 引用清理：删除连线时自动清理下游节点的 refFiles/refTexts/refAudios
 */
import React, { useState, useRef, memo } from 'react';
import { useReactFlow, useStore, getBezierPath, EdgeLabelRenderer } from 'reactflow';
import { Scissors } from 'lucide-react';
import * as C from './constants';

// 🎯 稳定的 zoom selector（只在 zoom 变化时触发重渲染，平移不触发）
const zoomSelector = (s) => s.transform[2];

const CustomEdge = memo(function CustomEdge({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, data = {} }) {
  const { setEdges, setNodes } = useReactFlow();
  const zoom = useStore(zoomSelector); // 获取当前画布缩放级别
  const [isHovered, setIsHovered] = useState(false);
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  
  const timerRef = useRef(null);
  const isLocked = data?.locked; 
  const isTransmitting = data?.isTransmitting; 

  const handleMouseEnter = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } setIsHovered(true); };
  const handleMouseLeave = () => { timerRef.current = setTimeout(() => { setIsHovered(false); }, 50); };

  const onEdgeClick = (evt) => {
    evt.stopPropagation(); evt.preventDefault();
    if (isLocked) return;
    
    // 删除连线并清理下游节点的引用数据
    setNodes((nds) => {
      const sourceNode = nds.find(n => n.id === source);
      if (!sourceNode) return nds;
      return nds.map(node => {
        if (node.id === target) {
          const newData = { ...node.data };
          if (sourceNode.data.type === 'text') newData.refTexts = (newData.refTexts || []).filter(t => t.id !== source);
          else if (sourceNode.data.type === 'audio') newData.refAudios = (newData.refAudios || []).filter(a => a.id !== source);
          else newData.refFiles = (newData.refFiles || []).filter(f => f.id !== source);
          return { ...node, data: newData };
        }
        return node;
      });
    });
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
  };

  // 🎯 抗缩放样式：将所有与"大小"相关的属性除以 zoom
  const edgeStyle = { 
    ...style, 
    stroke: style.stroke, 
    strokeWidth: (style.strokeWidth || 1.5) / zoom, // 📏 连线粗细恒定（默认 1.5px）
    opacity: style.opacity,
    filter: isTransmitting ? 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' : 'none' 
  };

  return (
    <g onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* 实际可见的连线路径 */}
      <path id={id} style={edgeStyle} className="react-flow__edge-path" d={edgePath} />
      
      {/* 加宽的透明触控区（20px 恒定宽度），方便鼠标悬停检测 */}
      <path d={edgePath} fill="none" stroke="transparent" strokeWidth={20 / zoom} style={{ cursor: isLocked ? 'default' : 'pointer' }} />
      
      {/* 📡 传输动画：两个白色圆点沿路径循环移动（半径 2.5px 恒定） */}
      {isTransmitting && (
        <>
          <circle r={2.5 / zoom} fill="#fff"><animateMotion dur="2s" repeatCount="indefinite" path={edgePath} calcMode="linear" /></circle>
          <circle r={2.5 / zoom} fill="#fff"><animateMotion dur="2s" begin="1s" repeatCount="indefinite" path={edgePath} calcMode="linear" /></circle>
        </>
      )}
      
      {isHovered && !isLocked && !isTransmitting && (
        <EdgeLabelRenderer>
          <div className="nodrag nopan" style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`, pointerEvents: 'all', zIndex: 1001, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={onEdgeClick}>
            <Scissors size={24} color={C.EDGE_COLOR_MANUAL} strokeWidth={1.5} style={{ filter: 'var(--edge-scissors-shadow)' }} />
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
});

export default CustomEdge;